/**
 * @license
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
//
// Vendored from rxfire 6.1.0 / packages/rxfire/storage/index.ts.
// rxfire has stagnated (last meaningful commit 2024-12-12) and pins firebase
// peer to ^9||^10||^11 — vendoring decouples us from those pins. Behavior is
// identical to upstream rxfire.

import type { UploadTask, UploadTaskSnapshot } from 'firebase/storage';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Observable<UploadTaskSnapshot> — emits each `state_changed` event from a
 * Firebase Storage upload task, plus the final task settlement (or error).
 */
export function fromTask(task: UploadTask): Observable<UploadTaskSnapshot> {
  return new Observable<UploadTaskSnapshot>((subscriber) => {
    let lastSnapshot: UploadTaskSnapshot | null = null;
    let complete = false;
    let hasError = false;
    let error: unknown = null;
    let id: ReturnType<typeof setTimeout> | null = null;

    // Schedules an async event to check and emit the most recent snapshot,
    // and complete or error if necessary.
    const schedule = (): void => {
      if (!id) {
        id = setTimeout(() => {
          id = null;
          if (lastSnapshot) subscriber.next(lastSnapshot);
          if (complete) subscriber.complete();
          if (hasError) subscriber.error(error);
        });
      }
    };
    const emit = (snapshot: UploadTaskSnapshot): void => {
      lastSnapshot = snapshot;
      schedule();
    };

    subscriber.add(() => {
      if (id) clearTimeout(id);
    });

    // Emit the initial snapshot
    emit(task.snapshot);
    // Each progress update
    subscriber.add(task.on('state_changed', emit));
    // task is a thenable — final emission / error / completion comes through
    // here. firebase/storage types `UploadTask.then` as non-generic returning
    // `Promise<unknown>`, so `from(task)` can't infer the inner type. Wrap in
    // a fresh Promise typed against UploadTaskSnapshot — the `then` callback's
    // first parameter is already typed as `UploadTaskSnapshot` upstream, so we
    // can pass `resolve` directly without a cast.
    const settle = new Promise<UploadTaskSnapshot>((resolve, reject) => {
      task.then((snapshot) => resolve(snapshot), reject);
    });
    subscriber.add(
      from(settle).subscribe({
        next: emit,
        error: (err: unknown) => {
          hasError = true;
          error = err;
          schedule();
        },
        complete: () => {
          complete = true;
          schedule();
        },
      }),
    );
  });
}

/**
 * Observable<{ progress, snapshot }> — like `fromTask`, but pre-computes the
 * percentage (0–100) for convenience.
 */
export function percentage(
  task: UploadTask,
): Observable<{ progress: number; snapshot: UploadTaskSnapshot }> {
  return fromTask(task).pipe(
    map((snapshot) => ({
      progress: (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
      snapshot,
    })),
  );
}
